import React, { useState, useEffect, useRef, useMemo } from 'react';
import Select from 'react-select';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    Plus,
    Edit2,
    Key,
    Lock,
    MapPin,
    Phone,
    Building2,
    ChevronRight,
    ChevronLeft,
    CreditCard,
    X,
    Sparkles,
    User,
    FileText,
    Download,
    Calendar,
    HelpCircle,
} from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import ModalInput from '../../Common/components/common/ModalInput';
import { useToast } from '../../Common/context/ToastContext';
import { useTooltip } from '../../Common/context/TooltipContext';
import { getAdminCpList, createAdminCp, getAdminCpDetail, updateAdminCp, resetAdminCpPassword } from '../api/cp';
import bankList from './bankList';

const baseUrl = process.env.REACT_APP_API || '';
const HANGUL_REGEX = /[ㄱ-ㅎㅏ-ㅣ가-힣]/g;
// TEST 값만 바꾸면 env 재시작 없이 화면 확인 가능(빌드에는 null로 두세요).
const TEST_MERCHANT_API_KEY = null; // 0 or 1
const USE_MERCHANT_API_KEY =
  TEST_MERCHANT_API_KEY != null
    ? Number(TEST_MERCHANT_API_KEY) === 1
    : process.env.REACT_APP_MERCHANT_API_KEY === '1';
const PG_PROVIDER = 'PAYDATAKR';

const bankSelectOptions = bankList.map((b) => ({ value: b.code, label: b.name }));

const stripHangul = (value) => String(value ?? '').replace(HANGUL_REGEX, '');

const bankSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '48px',
        borderRadius: '4px',
        borderColor: state.isFocused ? 'var(--brand-primary)' : 'transparent',
        backgroundColor: state.isFocused ? '#fff' : '#f2f4f6',
        boxShadow: state.isFocused ? '0 0 0 1px var(--brand-primary)' : 'none',
        fontSize: '15px',
        fontWeight: '500',
    }),
    singleValue: (base) => ({ ...base, fontSize: '15px', fontWeight: '500', color: '#191f28' }),
    input: (base) => ({ ...base, fontSize: '15px', fontWeight: '500', color: '#191f28' }),
    placeholder: (base) => ({ ...base, fontSize: '15px', fontWeight: '500', color: '#8b95a1' }),
    menu: (base) => ({ ...base, zIndex: 9999, maxHeight: '300px' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function MerchantManage_2() {
    const { showToast } = useToast();
    const { showAllOpen } = useTooltip();
    const [hoveredTooltipId, setHoveredTooltipId] = useState(null);
    const showToastRef = useRef(showToast);
    showToastRef.current = showToast;

    useEffect(() => {
        if (!showAllOpen) setHoveredTooltipId(null);
    }, [showAllOpen]);

    // 매출 데이터 배수 (테스트용: 1000000 = 백만 배, 원래대로 되돌리려면 1로 변경)
    const AMOUNT_MULTIPLIER = 1;

    // 월별 기간 조회
    const getDefaultMonth = () => {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    };
    const [selectedMonth, setSelectedMonth] = useState(getDefaultMonth());

    const [monthPickerOpen, setMonthPickerOpen] = useState(false);
    const monthPickerRef = useRef(null);

    const quickPeriod = (type) => {
        const today = new Date();
        let month;
        switch (type) {
            case '당월':
                month = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
                break;
            case '전월':
                const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                month = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
                break;
            default:
                return;
        }
        setSelectedMonth(month);
    };

    const changeMerchantMonth = (delta) => {
        if (!selectedMonth || !selectedMonth.match(/^\d{4}-\d{2}$/)) return;
        const [y, m] = selectedMonth.split('-').map(Number);
        const base = new Date(y, m - 1, 1);
        base.setMonth(base.getMonth() + delta);
        const mm = String(base.getMonth() + 1).padStart(2, '0');
        setSelectedMonth(`${base.getFullYear()}-${mm}`);
    };

    const handleMerchantMonthSelect = (date) => {
        if (!date) return;
        const y = date.getFullYear();
        const m = date.getMonth() + 1;
        const mm = String(m).padStart(2, '0');
        setSelectedMonth(`${y}-${mm}`);
        setMonthPickerOpen(false);
    };

    useEffect(() => {
        const handler = (e) => {
            if (monthPickerRef.current && !monthPickerRef.current.contains(e.target)) {
                setMonthPickerOpen(false);
            }
        };
        if (monthPickerOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [monthPickerOpen]);

    const [merchants, setMerchants] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedMerchantName, setSelectedMerchantName] = useState(null); // 세금계산서 행/가맹점 카드 클릭 시 동기화
    const [isNewMerchantModalOpen, setIsNewMerchantModalOpen] = useState(false);
    const [isEditMerchantModalOpen, setIsEditMerchantModalOpen] = useState(false);
    const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
    const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [passwordResetForm, setPasswordResetForm] = useState({ cpId: '', password: '' });
    const [apiKeyForm, setApiKeyForm] = useState({ cpId: '', appId: '', payKey: '' });
    const [isApiKeyLoading, setIsApiKeyLoading] = useState(false);
    const [isApiKeySaving, setIsApiKeySaving] = useState(false);
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [billingData, setBillingData] = useState([]);
    const [billingLoading, setBillingLoading] = useState(false);
    const [newMerchantForm, setNewMerchantForm] = useState({
        cpId: '',
        name: '',
        bossName: '',
        businessNo: '',
        phone: '',
        tempPassword: '',
        feeRate: '',
        address: '',
        bankName: '',
        bankCode: '',
        accountNumber: '',
        accountHolder: '',
    });

    // 모달 상태: 전체 모달 / 가맹점 정보 관련 모달 분리
    const isAnyModalOpen = isEditMerchantModalOpen || isNewMerchantModalOpen || isPasswordResetModalOpen || (USE_MERCHANT_API_KEY && isApiKeyModalOpen);
    const isMerchantInfoModalOpen = isEditMerchantModalOpen || isNewMerchantModalOpen;

    useEffect(() => {
        let cancelled = false;
        const fetchMerchants = async () => {
            setLoading(true);
            try {
                const data = await getAdminCpList({ page: 1, limit: 100 });
                const raw = data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
                const rows = raw.map((m, i) => ({
                    ...m,
                    id: m.id ?? m.cp_id ?? m.cpId ?? `M-${i}`,
                    cpId: m.cp_id ?? m.cpId ?? '',
                    name: m.name ?? '',
                    owner: m.boss_name ?? m.bossName ?? m.owner ?? m.ceo ?? '',
                    contact: m.phone ?? m.contact ?? '',
                    regNo: m.business_no ?? m.businessNo ?? m.regNo ?? '',
                    joinDate: m.created_at ?? m.joinDate ?? m.reg_date ?? '',
                    lastMonthSales: m.fee_rate ?? m.lastMonthSales ?? m.last_month_sales ?? '0',
                    status: m.status ?? '정상',
                    bankName: m.bank_name ?? m.bankName ?? '',
                    bankCode: m.bank_code ?? m.bankCode ?? '',
                    accountNumber: m.bank_account_number ?? m.bankAccountNumber ?? m.account_number ?? m.accountNumber ?? '',
                    accountHolder: m.bank_account_holder ?? m.bankAccountHolder ?? m.account_holder ?? m.accountHolder ?? '',
                    address: m.address ?? m.addr ?? '',
                }));
                if (!cancelled) setMerchants(rows);
            } catch (e) {
                if (!cancelled) {
                    showToastRef.current(e.message || '가맹점 목록을 불러오지 못했습니다.', 'error');
                    setMerchants([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchMerchants();
        return () => { cancelled = true; };
    }, []);

    // 월별 정산 데이터 조회
    useEffect(() => {
        if (!selectedMonth || !selectedMonth.match(/^\d{4}-\d{2}$/)) return;

        const fetchBillingData = async () => {
            setBillingLoading(true);
            try {
                const res = await axios.get(baseUrl + '/api/admin/cp-settlement/monthly-billing', {
                    params: {
                        month: selectedMonth,
                        includeEmpty: 0,
                        includeDetails: 0,
                    },
                    withCredentials: true,
                });
                let rows = [];
                if (res?.data?.code === 1000) {
                    const data = res.data?.data;
                    rows = Array.isArray(data?.rows) ? data.rows : [];
                }
                setBillingData(rows);
            } catch (e) {
                setBillingData([]);
            } finally {
                setBillingLoading(false);
            }
        };

        fetchBillingData();
    }, [selectedMonth]);

    // 가맹점명으로 정산 데이터 매칭 - AMOUNT_MULTIPLIER 적용
    const getBillingForMerchant = (merchantName) => {
        const found = billingData.find((b) => {
            const billingName = b.cp_name ?? b.cpName ?? '';
            return billingName === merchantName;
        });
        if (!found) return { paymentAmount: 0, settlementAmount: 0, fee: 0 };
        return {
            paymentAmount: Math.round(Number(found.payment_completed_amount ?? found.paymentCompletedAmount ?? 0)) * AMOUNT_MULTIPLIER,
            settlementAmount: Math.round(Number(found.settlement_net_amount ?? found.settlementNetAmount ?? 0)) * AMOUNT_MULTIPLIER,
            fee: Math.round(Number(found.payment_cp_fee_amount ?? found.paymentCpFeeAmount ?? found.settlement_fee_amount ?? 0)) * AMOUNT_MULTIPLIER,
        };
    };

    // 엑셀 다운로드 (가맹점명 기준 정렬)
    const downloadExcel = () => {
        const escapeCell = (v) => (v == null ? '' : String(v).replace(/"/g, '""'));
        const header = '"가맹점명","결제금액","정산금액","수수료율","수수료"';
        const sortedMerchants = [...merchants].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ko'));
        const lines = sortedMerchants.map((m) => {
            const billing = getBillingForMerchant(m.name);
            return [
                escapeCell(m.name),
                escapeCell(Number(billing.paymentAmount || 0).toLocaleString('ko-KR')),
                escapeCell(Number(billing.settlementAmount || 0).toLocaleString('ko-KR')),
                escapeCell(trimTrailingZeros(m.lastMonthSales) || '0'),
                escapeCell(Number(billing.fee || 0).toLocaleString('ko-KR')),
            ].map((c) => `"${c}"`).join(',');
        });
        const csv = '\uFEFF' + header + '\r\n' + lines.join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const monthLabel = selectedMonth.replace('-', '.');
        link.download = `가맹점매출_${monthLabel}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        showToastRef.current('엑셀 다운로드가 완료되었습니다.');
    };

    const formatBusinessNo = (value) => {
        const numbers = String(value ?? '').replace(/[^0-9]/g, '');
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
    };

    const formatPhone = (value) => {
        const nums = String(value ?? '').replace(/[^0-9]/g, '').slice(0, 11);
        if (nums.length <= 3) return nums;
        if (nums.startsWith('010')) {
            if (nums.length <= 7) return `${nums.slice(0, 3)}-${nums.slice(3)}`;
            return `${nums.slice(0, 3)}-${nums.slice(3, 7)}-${nums.slice(7, 11)}`;
        }
        if (nums.startsWith('02')) {
            if (nums.length <= 5) return `${nums.slice(0, 2)}-${nums.slice(2)}`;
            if (nums.length <= 9) return `${nums.slice(0, 2)}-${nums.slice(2, 5)}-${nums.slice(5, 9)}`;
            return `${nums.slice(0, 2)}-${nums.slice(2, 6)}-${nums.slice(6, 10)}`;
        }
        if (nums.length <= 6) return `${nums.slice(0, 3)}-${nums.slice(3)}`;
        if (nums.length <= 10) return `${nums.slice(0, 3)}-${nums.slice(3, 6)}-${nums.slice(6, 10)}`;
        return `${nums.slice(0, 3)}-${nums.slice(3, 7)}-${nums.slice(7, 11)}`;
    };

    const formatAccountShort = (value) => {
        const s = String(value ?? '').replace(/[^0-9]/g, '');
        if (s.length <= 8) return s;
        return `${s.slice(0, 4)}****${s.slice(-4)}`;
    };

    const trimTrailingZeros = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value).trim();
        if (!str.includes('.')) return str;
        const num = Number(str);
        if (!Number.isNaN(num)) return num.toString();
        return str.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
    };

    const openEditModal = async (merchant) => {
        setSelectedItem(merchant);
        try {
            const detail = await getAdminCpDetail(merchant.id);
            const d = detail?.cp ?? detail ?? {};
            setEditForm({
                id: d.id ?? merchant.id,
                cpId: d.cp_id ?? d.cpId ?? merchant.cpId ?? '',
                name: d.name ?? merchant.name ?? '',
                bossName: d.boss_name ?? d.bossName ?? merchant.owner ?? '',
                businessNo: d.business_no ?? d.businessNo ?? merchant.regNo ?? '',
                phone: d.phone ?? d.contact ?? merchant.contact ?? '',
                email: d.email ?? '',
                address: d.address ?? d.addr ?? merchant.address ?? '',
                feeRate: trimTrailingZeros(d.fee_rate ?? d.feeRate ?? merchant.lastMonthSales ?? ''),
                bankName: d.bank_name ?? d.bankName ?? merchant.bankName ?? '',
                bankCode: d.bank_code ?? d.bankCode ?? merchant.bankCode ?? '',
                accountNumber: d.bank_account_number ?? d.bankAccountNumber ?? merchant.accountNumber ?? '',
                accountHolder: d.bank_account_holder ?? d.bankAccountHolder ?? merchant.accountHolder ?? '',
            });
            setIsEditMerchantModalOpen(true);
        } catch (e) {
            showToastRef.current(e.message || '가맹점 상세정보를 불러오지 못했습니다.', 'error');
        }
    };

    const openPasswordModal = (merchant) => {
        setSelectedItem(merchant);
        setPasswordResetForm({ cpId: merchant.cpId ?? '', password: '' });
        setIsPasswordResetModalOpen(true);
    };

    const openApiKeyModal = async (merchant) => {
        const cpId = merchant.cpId ?? '';
        setSelectedItem(merchant);
        // 모달 오픈 직후 비우고, 조회 성공 시 실제 값을 input value로 채웁니다.
        setApiKeyForm({ cpId, appId: '', payKey: '' });
        setIsApiKeyModalOpen(true);
        try {
            setIsApiKeyLoading(true);
            const cpPkId = merchant.id ?? cpId;
            const res = await axios.get(baseUrl + '/api/admin/cp/pg-credential', {
                params: { cpId: cpPkId, pgProvider: PG_PROVIDER },
                withCredentials: true,
            });
            const credentialRows =
                res?.data?.data?.credentials ??
                res?.data?.data?.result ??
                res?.data?.credentials ??
                res?.data?.result ??
                [];
            const normalizedRows = Array.isArray(credentialRows) ? credentialRows : [];
            const appIdValue = normalizedRows.find((c) => String(c?.credential_key ?? c?.credentialKey ?? '').toLowerCase() === 'app_id')?.credential_value ?? '';
            const payKeyValue = normalizedRows.find((c) => String(c?.credential_key ?? c?.credentialKey ?? '').toLowerCase() === 'pay_key')?.credential_value ?? '';
            setApiKeyForm({ cpId, appId: String(appIdValue), payKey: String(payKeyValue) });
        } catch (e) {
            showToastRef.current(e?.response?.data?.msg || e?.message || 'API Key 조회에 실패했습니다.', 'error');
        } finally {
            setIsApiKeyLoading(false);
        }
    };

    // 표용 데이터 (가맹점명, 결제금액, 정산금액, 수수료율, 수수료) - 훅 규칙: 조건부 return 이전에 호출
    const tableData = useMemo(() => {
        return [...merchants]
            .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ko'))
            .map((m) => {
                const billing = getBillingForMerchant(m.name);
                return {
                    id: m.id,
                    name: m.name || '-',
                    paymentAmount: billing.paymentAmount,
                    settlementAmount: billing.settlementAmount,
                    feeRate: trimTrailingZeros(m.lastMonthSales) || '0',
                    fee: billing.fee,
                };
            });
    }, [merchants, billingData]);

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center py-20">
                <p className="text-[14px] text-[#8b95a1]">불러오는 중...</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col bg-white py-4 px-8 rounded-3xl overflow-hidden">
            {/* 페이지(목록) 영역 툴팁 */}
            <Tooltip
                id="merchant2-taxinvoice-tooltip"
                place="right"
                positionStrategy="absolute"
                className="max-w-[300px] text-[11px] z-[10000] whitespace-pre-line"
                isOpen={!isAnyModalOpen && (showAllOpen || hoveredTooltipId === 'merchant2-taxinvoice')}
                setIsOpen={() => { }}
            />
            <Tooltip
                id="merchant2-excel-download-tooltip"
                place="right"
                positionStrategy="absolute"
                className="max-w-[300px] text-[11px] z-[10000] whitespace-pre-line"
                isOpen={!isAnyModalOpen && (showAllOpen || hoveredTooltipId === 'merchant2-excel-download')}
                setIsOpen={() => { }}
            />
            <Tooltip
                id="merchant2-add-btn-tooltip"
                place="left"
                positionStrategy="absolute"
                className="max-w-[300px] text-[11px] z-[10000] whitespace-pre-line"
                isOpen={!isAnyModalOpen && (showAllOpen || hoveredTooltipId === 'merchant2-add-btn')}
                setIsOpen={() => { }}
            />
            <Tooltip
                id="merchant2-edit-btn-tooltip"
                place="left"
                positionStrategy="absolute"
                className="max-w-[300px] text-[11px] z-[10000] whitespace-pre-line"
                isOpen={!isAnyModalOpen && (showAllOpen || hoveredTooltipId === 'merchant2-edit-btn')}
                setIsOpen={() => { }}
            />
            <Tooltip
                id="merchant2-password-btn-tooltip"
                place="right"
                positionStrategy="absolute"
                className="max-w-[300px] text-[11px] z-[10000] whitespace-pre-line"
                isOpen={!isAnyModalOpen && (showAllOpen || hoveredTooltipId === 'merchant2-password-btn')}
                setIsOpen={() => { }}
            />
            {USE_MERCHANT_API_KEY && (
                <Tooltip
                    id="merchant2-api-key-btn-tooltip"
                    place="right"
                    positionStrategy="absolute"
                    className="max-w-[300px] text-[11px] z-[10000] whitespace-pre-line"
                    isOpen={!isAnyModalOpen && (showAllOpen || hoveredTooltipId === 'merchant2-api-key-btn')}
                    setIsOpen={() => { }}
                />
            )}
            {/* 모달 전용 툴팁 */}
            <Tooltip
                id="merchant2-id-tooltip"
                place="bottom"
                positionStrategy="absolute"
                className="max-w-[320px] text-[11px] z-[10050] whitespace-pre-line"
                // 가맹점 ID 툴팁은 "신규 가맹점 등록" 모달에서만 사용
                isOpen={isNewMerchantModalOpen && (showAllOpen || hoveredTooltipId === 'merchant2-id')}
                setIsOpen={() => { }}
            />
            <Tooltip
                id="merchant2-temp-password-tooltip"
                place="right"
                positionStrategy="absolute"
                className="max-w-[320px] text-[11px] z-[10050] whitespace-pre-line"
                isOpen={isNewMerchantModalOpen && (showAllOpen || hoveredTooltipId === 'merchant2-temp-password')}
                setIsOpen={() => { }}
            />
            <Tooltip
                id="merchant2-fee-tooltip"
                place="right"
                positionStrategy="absolute"
                className="max-w-[320px] text-[11px] z-[10050] whitespace-pre-line"
                isOpen={isMerchantInfoModalOpen && (showAllOpen || hoveredTooltipId === 'merchant2-fee')}
                setIsOpen={() => { }}
            />
            <Tooltip
                id="merchant2-account-tooltip"
                place="right"
                positionStrategy="absolute"
                className="max-w-[320px] text-[11px] z-[10050] whitespace-pre-line"
                isOpen={isMerchantInfoModalOpen && (showAllOpen || hoveredTooltipId === 'merchant2-account')}
                setIsOpen={() => { }}
            />
            {/* 상단: 세금계산서+기간조회+엑셀 | 총 파트너 수+신규 가맹점 추가 - 동일선상 */}
            <div className="flex justify-between items-center mb-4 shrink-0">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="shrink-0">
                        <h2 className="text-[18px] font-bold text-slate-800 flex items-center gap-1.5">
                            세금계산서
                            <span
                                data-tooltip-id="merchant2-taxinvoice-tooltip"
                                data-tooltip-html="가맹점에 발행할 월별 계산서 금액을 확인할 수 있습니다.<br/>신규 가맹점 추가 및 가맹점 정보를 변경할 수 있습니다."
                                onMouseEnter={() => setHoveredTooltipId('merchant2-taxinvoice')}
                                onMouseLeave={() => setHoveredTooltipId(null)}
                                className="inline-flex cursor-help text-slate-400 hover:text-slate-600 transition-colors"
                            >
                                <HelpCircle size={14} strokeWidth={2} />
                            </span>
                        </h2>
                        <p className="text-[11px] text-slate-400 font-medium mt-0.5">월별 세금계산서를 관리할 수 있습니다.</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <div ref={monthPickerRef} className="relative h-[36px] flex items-center gap-2 bg-white px-1 rounded-lg border border-[#e5e8eb] hover:border-[var(--brand-primary)]/30 transition-all">
                            <button
                                type="button"
                                onClick={() => changeMerchantMonth(-1)}
                                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#e5e8eb] text-[#4e5968] shrink-0"
                            >
                                <ChevronLeft size={14} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setMonthPickerOpen((v) => !v)}
                                className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-[#e5e8eb] text-[#191f28]"
                            >
                                <Calendar size={16} className="text-[var(--brand-primary)] shrink-0" />
                                <span className="text-[14px] font-medium tabular-nums">{selectedMonth || ''}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => changeMerchantMonth(1)}
                                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#e5e8eb] text-[#4e5968] shrink-0"
                            >
                                <ChevronRight size={14} />
                            </button>
                            {monthPickerOpen && (
                                <div className="absolute top-full left-0 mt-2 z-[1200] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                                    <DatePicker
                                        selected={selectedMonth ? new Date(selectedMonth + '-01') : new Date()}
                                        onChange={handleMerchantMonthSelect}
                                        showMonthYearPicker
                                        dateFormat="yyyy.MM"
                                        renderMonthContent={(month) => String(month + 1).padStart(2, '0')}
                                        inline
                                        calendarClassName="deposit-month-picker"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex gap-1">
                            {['당월'].map((type) => (
                                <button
                                    key={type}
                                    onClick={() => quickPeriod(type)}
                                    className="px-2.5 py-1.5 bg-white border border-[#e5e8eb] rounded-lg text-[12px] font-medium text-[#4e5968] hover:bg-[#f2f4f6] leading-none h-[36px] flex items-center"
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="h-4 w-px bg-gray-300 mx-1" />
                    <button
                        type="button"
                        data-tooltip-id="merchant2-excel-download-tooltip"
                        data-tooltip-html="가맹점 월별 세금계산서 내역을 다운로드합니다.<br/>세금계산서는 수수료금액을 vat포함으로 발행하시면 됩니다."
                        onMouseEnter={() => setHoveredTooltipId('merchant2-excel-download')}
                        onMouseLeave={() => setHoveredTooltipId(null)}
                        onClick={downloadExcel}
                        disabled={billingLoading || merchants.length === 0}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#191f28] text-white rounded-lg font-medium text-[14px] hover:bg-black transition-colors shrink-0 h-[40px] disabled:opacity-50 disabled:cursor-not-allowed"
                        title="엑셀 다운로드"
                    >
                        <Download size={16} /> 엑셀다운로드
                    </button>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <p className="text-[14px] font-medium text-[#8b95a1]">
                        총 <span className="text-[var(--brand-primary)] font-medium">{merchants.length}</span>개 파트너
                    </p>
                    <div className="h-4 w-px bg-gray-200 mx-0.5" />
                    <span
                        data-tooltip-id="merchant2-add-btn-tooltip"
                        data-tooltip-html="새 가맹점을 등록하고 ID와 임시 비밀번호를 설정합니다.<br/>가맹점 ID는 등록 후 변경할 수 없습니다."
                        onMouseEnter={() => setHoveredTooltipId('merchant2-add-btn')}
                        onMouseLeave={() => setHoveredTooltipId(null)}
                        className="inline-flex"
                    >
                        <button
                            type="button"
                            onClick={() => setIsNewMerchantModalOpen(true)}
                            className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-all shadow-md shadow-[var(--brand-primary-border)] active:scale-95 text-[15px] h-[40px]"
                        >
                            <Plus size={20} />
                            신규 가맹점 추가
                        </button>
                    </span>
                </div>
            </div>

            {/* 양쪽 분할: 왼쪽 40% 표만, 오른쪽 60% 가맹점 목록 */}
            <div className="flex-1 min-h-0 flex gap-6 overflow-hidden">
                {/* 왼쪽 40%: 세금계산서 표 */}
                <div className="w-[571px] flex flex-col min-w-0 overflow-hidden bg-white rounded-2xl border border-[#e0e0e0] shadow-md">
                    <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0">
                        <table className="w-full text-left text-[12px] table-fixed">
                            <thead className="bg-[#f9fafb] text-[#8b95a1] font-medium text-[12px] sticky top-0 z-10 border-b border-[#f2f4f6]">
                                <tr>
                                    <th className="px-3 py-2">가맹점명</th>
                                    <th className="px-3 py-2 text-right w-[120px]">결제금액</th>
                                    <th className="px-3 py-2 text-right w-[120px]">정산금액</th>
                                    <th className="px-3 py-2 text-right w-[70px]">수수료율</th>
                                    <th className="px-3 py-2 text-right w-[110px]">수수료</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#f2f4f6]">
                                {!tableData?.length ? (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-12 text-center text-[15px] font-medium text-[#8b95a1]">
                                            데이터가 없습니다.
                                        </td>
                                    </tr>
                                ) : (
                                    tableData.map((row) => {
                                        const isSelected = selectedMerchantName === row.name;
                                        return (
                                            <tr
                                                key={row.id}
                                                onClick={() => setSelectedMerchantName(row.name)}
                                                className={`cursor-pointer relative ${isSelected ? 'bg-[#f8fbff] z-10 shadow-[0_4px_12px_rgba(0,0,0,0.1)]' : 'hover:bg-[#f9fafb]'
                                                    }`}
                                                style={isSelected ? { outline: '2px solid var(--brand-primary)', outlineOffset: '-2px' } : {}}
                                            >
                                                <td className="px-3 py-2 text-[12px] text-[#4e5968] font-medium min-w-0 overflow-hidden">
                                                    <span className="inline-flex items-center gap-1 min-w-0 w-full">
                                                        <span className="truncate" title={row.name}>{row.name}</span>
                                                        <ChevronRight size={14} className="text-[#8b95a1] shrink-0" />
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-[12px] text-right text-[#191f28] tabular-nums whitespace-nowrap">{row.paymentAmount.toLocaleString('ko-KR')}원</td>
                                                <td className="px-3 py-2 text-[12px] text-right text-[#191f28] tabular-nums whitespace-nowrap">{row.settlementAmount.toLocaleString('ko-KR')}원</td>
                                                <td className="px-3 py-2 text-[12px] text-right text-[#4e5968] tabular-nums whitespace-nowrap">{row.feeRate}%</td>
                                                <td className="px-3 py-2 text-[12px] text-right text-[var(--brand-primary)] font-medium tabular-nums whitespace-nowrap">{row.fee.toLocaleString('ko-KR')}원</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 오른쪽 60%: 가맹점 목록 카드 */}
                <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden pr-1 custom-scrollbar">
                    <div className="grid grid-cols-4 xl:grid-cols-4 gap-3">
                        {[...merchants].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ko')).map((merchant) => {
                            const isCardSelected = selectedMerchantName === merchant.name;
                            return (
                                <div
                                    key={merchant.id}
                                    onClick={() => setSelectedMerchantName(merchant.name)}
                                    className={`min-w-0 min-h-[215px] bg-white rounded-2xl flex flex-col cursor-pointer overflow-hidden relative border-2  ${isCardSelected
                                        ? 'border-[var(--brand-primary)] z-10 shadow-[0_4px_12px_rgba(0,0,0,0.1)]'
                                        : 'border-gray-200 shadow-sm hover:shadow-lg hover:border-[var(--brand-primary)]/30'
                                        }`}
                                >
                                    <div className="px-4 pt-3 pb-2 bg-gray-50">
                                        <div className="flex justify-between items-start gap-2">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className="min-w-0">
                                                    <h2 className="text-[16px] font-bold text-gray-900 flex items-center gap-1 min-w-0">
                                                        <span className="truncate" title={merchant.name}>{merchant.name}</span>
                                                        <ChevronRight size={16} className="text-[#8b95a1] shrink-0" />
                                                    </h2>
                                                    <p className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1 whitespace-nowrap min-w-0">
                                                        <span className="font-mono truncate" title={`ID: ${merchant.cpId || merchant.id}`}>
                                                            ID: {merchant.cpId || merchant.id}
                                                        </span>
                                                        {/* <span className="text-gray-300">·</span>
                                                        <span className="font-medium tabular-nums text-[10px]">
                                                            가입: {merchant.joinDate ? String(merchant.joinDate).split(' ')[0] : '-'}
                                                        </span> */}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex gap-1 shrink-0">
                                                {USE_MERCHANT_API_KEY && (
                                                    <span
                                                        data-tooltip-id="merchant2-api-key-btn-tooltip"
                                                        data-tooltip-html="가맹점 API Key를 관리합니다."
                                                        onMouseEnter={() => setHoveredTooltipId('merchant2-api-key-btn')}
                                                        onMouseLeave={() => setHoveredTooltipId(null)}
                                                        className="inline-flex"
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); openApiKeyModal(merchant); }}
                                                            className="p-2 text-gray-400 hover:text-[var(--brand-primary)] hover:bg-white rounded-lg transition-all border border-transparent hover:border-gray-100"
                                                        >
                                                            <Key size={16} />
                                                        </button>
                                                    </span>
                                                )}
                                                <span
                                                    data-tooltip-id="merchant2-edit-btn-tooltip"
                                                    data-tooltip-html="가맹점 정보를 수정합니다."
                                                    onMouseEnter={() => setHoveredTooltipId('merchant2-edit-btn')}
                                                    onMouseLeave={() => setHoveredTooltipId(null)}
                                                    className="inline-flex"
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); openEditModal(merchant); }}
                                                        className="p-2 text-gray-400 hover:text-[var(--brand-primary)] hover:bg-white rounded-lg transition-all border border-transparent hover:border-gray-100"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                </span>
                                                <span
                                                    data-tooltip-id="merchant2-password-btn-tooltip"
                                                    data-tooltip-html="가맹점 비밀번호를 변경합니다.<br/>가맹점 ID는 변경할 수 없습니다."
                                                    onMouseEnter={() => setHoveredTooltipId('merchant2-password-btn')}
                                                    onMouseLeave={() => setHoveredTooltipId(null)}
                                                    className="inline-flex"
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); openPasswordModal(merchant); }}
                                                        className="p-2 text-gray-400 hover:text-orange-600 hover:bg-white rounded-lg transition-all border border-transparent hover:border-gray-100"
                                                    >
                                                        <Lock size={16} />
                                                    </button>
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="px-4 py-2 relative">
                                        {/* 수수료 - position absolute */}
                                        <div className="absolute top-1 right-4">
                                            <p className="text-[18px] font-medium text-[var(--brand-primary)] tabular-nums">{trimTrailingZeros(merchant.lastMonthSales) || '0'}%</p>
                                        </div>

                                        {/* 이름, 사업자번호, 전화번호, 주소 - 동일 간격 */}
                                        <div className="flex flex-col gap-1.5">
                                            <div className="flex items-center gap-2 text-[13px] text-gray-600">
                                                <User size={13} className="text-gray-300 shrink-0" />
                                                <span className="font-medium text-gray-800 truncate">{merchant.owner || '-'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-[13px] text-gray-600">
                                                <FileText size={13} className="text-gray-300 shrink-0" />
                                                <span className="font-medium text-gray-700 tabular-nums">{formatBusinessNo(merchant.regNo) || '-'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-[13px] text-gray-600">
                                                <Phone size={13} className="text-gray-300 shrink-0" />
                                                <span className="font-medium tabular-nums">{formatPhone(merchant.contact) || '-'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-[13px] text-gray-600">
                                                <MapPin size={13} className="text-gray-300 shrink-0" />
                                                <span className="truncate whitespace-nowrap" title={merchant.address || '-'}>
                                                    {merchant.address || '-'}
                                                </span>
                                            </div>
                                            {/* 계좌정보 한줄: 은행|계좌|예금주 (줄바꿈 없음, 계좌 축약, hover 시 전체) */}
                                            <div
                                                className="flex items-center gap-1.5 flex-nowrap text-[13px] overflow-hidden min-w-0 pt-2 border-t border-gray-100"
                                                title={`은행 ${merchant.bankName || '-'} / 계좌 ${merchant.accountNumber || '-'} / 예금주 ${merchant.accountHolder || '-'}`}
                                            >
                                                <Building2 size={14} className="text-[var(--brand-primary)] shrink-0" />
                                                <span className="font-medium text-gray-700 whitespace-nowrap truncate min-w-0">
                                                    {merchant.bankName || '-'}
                                                    <span className="text-gray-300 mx-1">|</span>
                                                    {merchant.accountNumber ? formatAccountShort(merchant.accountNumber) : '-'}
                                                    <span className="text-gray-300 mx-1">|</span>
                                                    {merchant.accountHolder || '-'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {merchants.length < 10 && (
                            <button
                                type="button"
                                onClick={() => setIsNewMerchantModalOpen(true)}
                                className="min-h-[215px] border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center p-4 text-center hover:border-[var(--brand-primary)]/50 hover:bg-[var(--brand-primary-soft)]/50 transition-all cursor-pointer"
                            >
                                <div className="w-12 h-12 bg-white shadow-sm border border-gray-100 text-gray-300 rounded-xl flex items-center justify-center mb-1.5 group-hover:scale-110 group-hover:text-[var(--brand-primary)] transition-all">
                                    <Plus size={24} />
                                </div>
                                <p className="text-[13px] text-gray-400 font-medium uppercase tracking-widest group-hover:text-[var(--brand-primary)]">Add New</p>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* 가맹점 수정 모달 */}
            {isEditMerchantModalOpen && selectedItem && editForm && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="bg-white w-[540px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
                        <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
                            <h2 className="text-[24px] font-medium text-[#191f28]">가맹점 정보 수정</h2>
                            <button type="button" onClick={() => setIsEditMerchantModalOpen(false)} className="p-2 text-[#adb5bd] hover:text-[#191f28]">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="py-5 px-8 grid grid-cols-2 gap-2 overflow-y-auto max-h-[700px] custom-scrollbar">
                            <div className="col-span-2">
                                <div className="p-4 bg-[var(--brand-primary-soft)] rounded-2xl flex items-center gap-3 border border-[var(--brand-primary-border)]">
                                    <div className="p-2 bg-white rounded-lg"><Sparkles size={16} className="text-[var(--brand-primary)]" /></div>
                                    <span className="text-[13px] font-medium text-[var(--brand-primary)]">가맹점 정보를 수정합니다.</span>
                                </div>
                            </div>
                            <ModalInput label="가맹점명" placeholder="예: 도매상" value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} />
                            <ModalInput label="대표자명" placeholder="성함을 입력하세요" value={editForm.bossName} onChange={(e) => setEditForm((p) => ({ ...p, bossName: e.target.value }))} />
                            <ModalInput label="사업자등록번호" placeholder="000-00-00000" value={formatBusinessNo(editForm.businessNo)} maxLength={12} onChange={(e) => setEditForm((p) => ({ ...p, businessNo: e.target.value.replace(/[^0-9]/g, '').slice(0, 10) }))} />
                            <ModalInput label="연락처" placeholder="010-0000-0000" value={formatPhone(editForm.phone)} onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value.replace(/[^0-9]/g, '').slice(0, 11) }))} />
                            <div className="col-span-2">
                                <ModalInput label="사업장 주소" placeholder="사업장 주소 입력" value={editForm.address ?? ''} onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))} />
                            </div>
                            <ModalInput
                                label={
                                    <>
                                        수수료(부가세포함)%
                                        <span
                                            data-tooltip-id="merchant2-fee-tooltip"
                                            data-tooltip-html="설정된 수수료만큼 제외된 금액이 가맹점으로 입금됩니다."
                                            onMouseEnter={() => setHoveredTooltipId('merchant2-fee')}
                                            onMouseLeave={() => setHoveredTooltipId(null)}
                                            className="inline-flex cursor-help text-slate-400 hover:text-slate-600"
                                        >
                                            <HelpCircle size={12} strokeWidth={2} />
                                        </span>
                                    </>
                                }
                                placeholder="예: 2.9"
                                value={editForm.feeRate ?? ''}
                                onChange={(e) => setEditForm((p) => ({ ...p, feeRate: e.target.value.replace(/[^0-9.]/g, '') }))}
                            />
                            <div className="flex flex-col gap-2">
                                <label className="text-[13px] font-medium text-[#8b95a1]">은행</label>
                                <Select options={bankSelectOptions} value={bankSelectOptions.find((o) => o.value === (editForm.bankCode ?? '')) ?? null} onChange={(opt) => setEditForm((p) => ({ ...p, bankCode: opt?.value ?? '', bankName: opt?.label ?? '' }))} isSearchable isClearable placeholder="은행 검색 또는 선택" noOptionsMessage={() => '검색 결과 없음'} styles={bankSelectStyles} classNamePrefix="bank-select" menuPortalTarget={document.body} menuPosition="fixed" />
                            </div>
                            <ModalInput
                                label={
                                    <>
                                        계좌번호
                                        <span
                                            data-tooltip-id="merchant2-account-tooltip"
                                            data-tooltip-html="기재된 계좌번호로 정산금액이 입금됩니다.<br/>계좌번호 변경은 관리자만 가능합니다."
                                            onMouseEnter={() => setHoveredTooltipId('merchant2-account')}
                                            onMouseLeave={() => setHoveredTooltipId(null)}
                                            className="inline-flex cursor-help text-slate-400 hover:text-slate-600"
                                        >
                                            <HelpCircle size={12} strokeWidth={2} />
                                        </span>
                                    </>
                                }
                                placeholder="예: 123-456-789012"
                                value={editForm.accountNumber ?? ''}
                                onChange={(e) => setEditForm((p) => ({ ...p, accountNumber: e.target.value.replace(/[^0-9-]/g, '') }))}
                            />
                            <ModalInput label="예금주" placeholder="예금주명 입력" value={editForm.accountHolder ?? ''} onChange={(e) => setEditForm((p) => ({ ...p, accountHolder: e.target.value }))} />
                        </div>
                        <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
                            <button type="button" onClick={() => { setIsEditMerchantModalOpen(false); setEditForm(null); }} className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-medium text-[16px]">취소</button>
                            <button
                                type="button"
                                onClick={async () => {
                                    if (!editForm?.id) { showToastRef.current('가맹점 식별 ID가 없습니다.', 'error'); return; }
                                    const businessNoNumbers = (editForm.businessNo ?? '').trim().replace(/[^0-9]/g, '');
                                    if (!editForm.name?.trim() || !editForm.bossName?.trim() || !businessNoNumbers || businessNoNumbers.length !== 10 || !editForm.phone?.trim()) {
                                        showToastRef.current('가맹점명, 대표자명, 사업자등록번호(10자리), 연락처는 필수입니다.', 'error');
                                        return;
                                    }
                                    try {
                                        const body = { id: editForm.id, name: editForm.name.trim(), bossName: editForm.bossName.trim(), businessNo: businessNoNumbers, phone: editForm.phone.trim(), address: (editForm.address ?? '').trim(), feeRate: (editForm.feeRate ?? '').trim(), bankName: (editForm.bankName ?? '').trim(), bankCode: (editForm.bankCode ?? '').trim(), accountNumber: (editForm.accountNumber ?? '').trim(), accountHolder: (editForm.accountHolder ?? '').trim() };
                                        await updateAdminCp(body);
                                        showToastRef.current('가맹점 정보가 성공적으로 수정되었습니다.');
                                        setIsEditMerchantModalOpen(false);
                                        setEditForm(null);
                                        setMerchants((prev) => prev.map((m) => m.id === body.id ? { ...m, name: body.name, owner: body.bossName, contact: body.phone, regNo: businessNoNumbers, address: body.address ?? m.address, lastMonthSales: body.feeRate ?? m.lastMonthSales, bankName: body.bankName, bankCode: body.bankCode, accountNumber: body.accountNumber, accountHolder: body.accountHolder } : m));
                                        setSelectedItem((prev) => prev && prev.id === body.id ? { ...prev, name: body.name, owner: body.bossName, contact: body.phone, regNo: businessNoNumbers, address: body.address ?? prev.address, lastMonthSales: body.feeRate ?? prev.lastMonthSales, bankName: body.bankName, bankCode: body.bankCode, accountNumber: body.accountNumber, accountHolder: body.accountHolder } : prev);
                                    } catch (e) {
                                        showToastRef.current(e.message || '가맹점 정보 수정에 실패했습니다.', 'error');
                                    }
                                }}
                                className="flex-1 py-4 bg-[var(--brand-primary)] text-white rounded-2xl font-medium text-[16px] shadow-lg shadow-[var(--brand-primary)]/20"
                            >
                                수정 완료
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 신규 가맹점 등록 모달 */}
            {isNewMerchantModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="bg-white w-[540px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
                        <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
                            <h2 className="text-[24px] font-medium text-[#191f28]">신규 가맹점 등록</h2>
                            <button type="button" onClick={() => setIsNewMerchantModalOpen(false)} className="p-2 text-[#adb5bd] hover:text-[#191f28]"><X size={24} /></button>
                        </div>
                        <div className="py-5 px-8 grid grid-cols-2 gap-2 overflow-y-auto max-h-[700px] custom-scrollbar">
                            <div className="col-span-2">
                                <div className="p-4 bg-[var(--brand-primary-soft)] rounded-2xl flex items-center gap-3 border border-[var(--brand-primary-border)]">
                                    <div className="p-2 bg-white rounded-lg"><Sparkles size={16} className="text-[var(--brand-primary)]" /></div>
                                    <span className="text-[13px] font-medium text-[var(--brand-primary)]">가맹점의 신규 계정 정보를 생성합니다.</span>
                                </div>
                            </div>
                            <div className="col-span-2 grid grid-cols-2 gap-2 pb-4 border-b border-[#e5e8eb]">
                                <ModalInput
                                    label={
                                        <>
                                            가맹점 아이디 (ID)
                                            <span
                                                data-tooltip-id="merchant2-id-tooltip"
                                                data-tooltip-html="가맹점 로그인에 사용하는 ID입니다.<br/>영업자 ID는 가맹점 ID 뒤에 '_d'를 붙여 {`{가맹점ID}_d`} 형태로 생성됩니다."
                                                onMouseEnter={() => setHoveredTooltipId('merchant2-id')}
                                                onMouseLeave={() => setHoveredTooltipId(null)}
                                                className="inline-flex cursor-help text-slate-400 hover:text-slate-600"
                                            >
                                                <HelpCircle size={12} strokeWidth={2} />
                                            </span>
                                        </>
                                    }
                                    placeholder="cp001"
                                    icon={<User size={16} />}
                                    value={newMerchantForm.cpId}
                                    onChange={(e) => setNewMerchantForm((p) => ({ ...p, cpId: stripHangul(e.target.value) }))}
                                />
                                <ModalInput
                                    label={
                                        <>
                                            초기 임시비밀번호
                                            <span
                                                data-tooltip-id="merchant2-temp-password-tooltip"
                                                data-tooltip-html="가맹점 초기 임시비밀번호를 설정합니다.<br/>영업자 비밀번호는 가맹점페이지에서 수정가능합니다."
                                                onMouseEnter={() => setHoveredTooltipId('merchant2-temp-password')}
                                                onMouseLeave={() => setHoveredTooltipId(null)}
                                                className="inline-flex cursor-help text-slate-400 hover:text-slate-600"
                                            >
                                                <HelpCircle size={12} strokeWidth={2} />
                                            </span>
                                        </>
                                    }
                                    placeholder="TempPass123!"
                                    type="password"
                                    icon={<Lock size={16} />}
                                    value={newMerchantForm.tempPassword}
                                    onChange={(e) => setNewMerchantForm((p) => ({ ...p, tempPassword: e.target.value }))}
                                />
                            </div>
                            <ModalInput label="가맹점명" placeholder="예: 도매상" value={newMerchantForm.name} onChange={(e) => setNewMerchantForm((p) => ({ ...p, name: e.target.value }))} />
                            <ModalInput label="대표자명" placeholder="성함을 입력하세요" value={newMerchantForm.bossName} onChange={(e) => setNewMerchantForm((p) => ({ ...p, bossName: e.target.value }))} />
                            <ModalInput label="사업자등록번호" placeholder="123-45-67890" value={formatBusinessNo(newMerchantForm.businessNo)} maxLength={12} onChange={(e) => setNewMerchantForm((p) => ({ ...p, businessNo: e.target.value.replace(/[^0-9]/g, '').slice(0, 10) }))} />
                            <ModalInput label="연락처" placeholder="010-1234-5678" value={formatPhone(newMerchantForm.phone)} onChange={(e) => setNewMerchantForm((p) => ({ ...p, phone: e.target.value.replace(/[^0-9]/g, '').slice(0, 11) }))} />
                            <div className="col-span-2"><ModalInput label="사업장 주소" placeholder="사업장 주소 입력" value={newMerchantForm.address} onChange={(e) => setNewMerchantForm((p) => ({ ...p, address: e.target.value }))} /></div>
                            <ModalInput
                                label={
                                    <>
                                        수수료(부가세포함)%
                                        <span
                                            data-tooltip-id="merchant2-fee-tooltip"
                                            data-tooltip-html="설정된 수수료만큼 제외된 금액이 가맹점으로 입금됩니다."
                                            onMouseEnter={() => setHoveredTooltipId('merchant2-fee')}
                                            onMouseLeave={() => setHoveredTooltipId(null)}
                                            className="inline-flex cursor-help text-slate-400 hover:text-slate-600"
                                        >
                                            <HelpCircle size={12} strokeWidth={2} />
                                        </span>
                                    </>
                                }
                                placeholder="3"
                                type="number"
                                step="0.01"
                                value={newMerchantForm.feeRate}
                                onChange={(e) => setNewMerchantForm((p) => ({ ...p, feeRate: e.target.value }))}
                            />
                            <div className="flex flex-col gap-2">
                                <label className="text-[13px] font-medium text-[#8b95a1]">은행</label>
                                <Select options={bankSelectOptions} value={bankSelectOptions.find((o) => o.value === newMerchantForm.bankCode) ?? null} onChange={(opt) => setNewMerchantForm((p) => ({ ...p, bankCode: opt?.value ?? '', bankName: opt?.label ?? '' }))} isSearchable isClearable placeholder="은행 검색 또는 선택" noOptionsMessage={() => '검색 결과 없음'} styles={bankSelectStyles} classNamePrefix="bank-select" menuPortalTarget={document.body} menuPosition="fixed" />
                            </div>
                            <ModalInput
                                label={
                                    <>
                                        계좌번호
                                        <span
                                            data-tooltip-id="merchant2-account-tooltip"
                                            data-tooltip-html="기재된 계좌번호로 정산금액이 입금됩니다.<br/>계좌번호 변경은 관리자만 가능합니다."
                                            onMouseEnter={() => setHoveredTooltipId('merchant2-account')}
                                            onMouseLeave={() => setHoveredTooltipId(null)}
                                            className="inline-flex cursor-help text-slate-400 hover:text-slate-600"
                                        >
                                            <HelpCircle size={12} strokeWidth={2} />
                                        </span>
                                    </>
                                }
                                placeholder="예: 123-456-789012"
                                value={newMerchantForm.accountNumber}
                                onChange={(e) => setNewMerchantForm((p) => ({ ...p, accountNumber: e.target.value.replace(/[^0-9-]/g, '') }))}
                            />
                            <ModalInput label="예금주" placeholder="예금주명 입력" value={newMerchantForm.accountHolder} onChange={(e) => setNewMerchantForm((p) => ({ ...p, accountHolder: e.target.value }))} />
                        </div>
                        <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
                            <button type="button" onClick={() => setIsNewMerchantModalOpen(false)} className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-medium text-[16px]">취소</button>
                            <button
                                type="button"
                                onClick={async () => {
                                    const { cpId, name, bossName, businessNo, phone, tempPassword, feeRate, address, bankName, bankCode, accountNumber, accountHolder } = newMerchantForm;
                                    const normalizedCpId = stripHangul(cpId).trim();
                                    const businessNoNumbers = (businessNo ?? '').trim().replace(/[^0-9]/g, '');
                                    if (!normalizedCpId || !name?.trim() || !bossName?.trim() || !businessNoNumbers || businessNoNumbers.length !== 10 || !phone?.trim() || !tempPassword?.trim() || !feeRate?.trim()) {
                                        showToastRef.current('가맹점 ID, 가맹점명, 대표자명, 사업자등록번호(10자리), 연락처, 임시비밀번호, 결제 수수료는 필수입니다.', 'error');
                                        return;
                                    }
                                    if (!bankName?.trim() || !bankCode?.trim() || !accountNumber?.trim() || !accountHolder?.trim()) {
                                        showToastRef.current('은행명, 은행코드, 계좌번호, 예금주를 모두 입력해 주세요.', 'error');
                                        return;
                                    }
                                    const feeRateNum = Number(feeRate);
                                    if (isNaN(feeRateNum) || feeRateNum < 0 || feeRateNum > 100) {
                                        showToastRef.current('결제 수수료는 0~100 사이의 숫자여야 합니다.', 'error');
                                        return;
                                    }
                                    try {
                                        const body = { cpId: normalizedCpId, name: name.trim(), bossName: bossName.trim(), businessNo: businessNoNumbers, phone: phone.trim(), tempPassword: tempPassword.trim(), feeRate: feeRateNum, address: (address ?? '').trim(), bankName: bankName.trim(), bankCode: bankCode.trim(), accountNumber: accountNumber.trim(), accountHolder: accountHolder.trim() };
                                        const result = await createAdminCp(body);
                                        showToastRef.current(`신규 가맹점이 성공적으로 등록되었습니다.${result?.tempPassword ? ` 임시비밀번호: ${result.tempPassword}` : ''}`);
                                        setIsNewMerchantModalOpen(false);
                                        setNewMerchantForm({ cpId: '', name: '', bossName: '', businessNo: '', phone: '', tempPassword: '', feeRate: '', address: '', bankName: '', bankCode: '', accountNumber: '', accountHolder: '' });
                                        const data = await getAdminCpList({ page: 1, limit: 100 });
                                        const raw = data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
                                        setMerchants(raw.map((m, i) => ({
                                            ...m, id: m.id ?? m.cp_id ?? m.cpId ?? `M-${i}`, cpId: m.cp_id ?? m.cpId ?? '', name: m.name ?? '', owner: m.boss_name ?? m.bossName ?? m.owner ?? '', contact: m.phone ?? m.contact ?? '', regNo: m.business_no ?? m.businessNo ?? m.regNo ?? '', joinDate: m.created_at ?? m.joinDate ?? m.reg_date ?? '', lastMonthSales: m.fee_rate ?? m.lastMonthSales ?? '0', status: m.status ?? '정상', bankName: m.bank_name ?? m.bankName ?? '', bankCode: m.bank_code ?? m.bankCode ?? '', accountNumber: m.bank_account_number ?? m.bankAccountNumber ?? m.account_number ?? m.accountNumber ?? '', accountHolder: m.bank_account_holder ?? m.bankAccountHolder ?? m.account_holder ?? m.accountHolder ?? '', address: m.address ?? m.addr ?? ''
                                        })));
                                    } catch (e) {
                                        showToastRef.current(e.message || '신규 가맹점 등록에 실패했습니다.', 'error');
                                    }
                                }}
                                className="flex-1 py-4 bg-[var(--brand-primary)] text-white rounded-2xl font-medium text-[16px] shadow-lg shadow-[var(--brand-primary)]/20"
                            >
                                등록
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* API Key 관리 모달 */}
            {USE_MERCHANT_API_KEY && isApiKeyModalOpen && selectedItem && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="bg-white w-[480px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
                        <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
                            <h2 className="text-[24px] font-medium text-[#191f28]">API Key 관리</h2>
                            <button type="button" onClick={() => { setIsApiKeyModalOpen(false); setApiKeyForm({ cpId: '', appId: '', payKey: '' }); }} className="p-2 text-[#adb5bd] hover:text-[#191f28]"><X size={24} /></button>
                        </div>
                        <div className="py-5 px-8 space-y-4">
                            <div className="p-4 bg-[var(--brand-primary-soft)] rounded-2xl flex items-center gap-3 border border-[var(--brand-primary-border)]">
                                <div className="p-2 bg-white rounded-lg"><Key size={16} className="text-[var(--brand-primary)]" /></div>
                                <span className="text-[13px] font-medium text-[var(--brand-primary)]">한국결제데이터 API Key를 관리합니다.</span>
                            </div>
                            <ModalInput
                                label="app_id"
                                placeholder="app_id 입력"
                                icon={<Key size={16} />}
                                value={apiKeyForm.appId}
                                onChange={(e) => setApiKeyForm((p) => ({ ...p, appId: e.target.value }))}
                            />
                            <ModalInput
                                label="pay_key"
                                placeholder="pay_key 입력"
                                icon={<Lock size={16} />}
                                value={apiKeyForm.payKey}
                                onChange={(e) => setApiKeyForm((p) => ({ ...p, payKey: e.target.value }))}
                            />
                            {isApiKeyLoading && (
                                <p className="text-[12px] text-[#8b95a1]">기존 자격증명 조회 중...</p>
                            )}
                        </div>
                        <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
                            <button type="button" disabled={isApiKeySaving} onClick={() => { setIsApiKeyModalOpen(false); setApiKeyForm({ cpId: '', appId: '', payKey: '' }); }} className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-medium text-[16px] disabled:opacity-50 disabled:cursor-not-allowed">취소</button>
                            <button
                                type="button"
                                disabled={isApiKeySaving}
                                onClick={async () => {
                                    if (!apiKeyForm.cpId?.trim()) { showToastRef.current('가맹점 ID가 없습니다.', 'error'); return; }
                                    const appId = (apiKeyForm.appId ?? '').trim();
                                    const payKey = (apiKeyForm.payKey ?? '').trim();
                                    if (!appId || !payKey) { showToastRef.current('app_id, pay_key를 모두 입력해 주세요.', 'error'); return; }
                                    try {
                                        setIsApiKeySaving(true);
                                        const cpPkId = selectedItem?.id ?? apiKeyForm.cpId.trim();
                                        await axios.post(
                                            baseUrl + '/api/admin/cp/pg-credential',
                                            {
                                                cpId: cpPkId,
                                                pgProvider: PG_PROVIDER,
                                                credentials: { app_id: appId, pay_key: payKey },
                                            },
                                            { withCredentials: true },
                                        );
                                        showToastRef.current('PG 자격증명이 저장되었습니다.');
                                        setIsApiKeyModalOpen(false);
                                        setApiKeyForm({ cpId: '', appId: '', payKey: '' });
                                    } catch (e) {
                                        showToastRef.current(e?.response?.data?.msg || e?.message || 'PG 자격증명 저장에 실패했습니다.', 'error');
                                    } finally {
                                        setIsApiKeySaving(false);
                                    }
                                }}
                                className="flex-1 py-4 bg-[var(--brand-primary)] text-white rounded-2xl font-medium text-[16px] shadow-lg shadow-[var(--brand-primary)]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isApiKeySaving ? '저장 중...' : '저장'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 비밀번호 변경 모달 */}
            {isPasswordResetModalOpen && selectedItem && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="bg-white w-[480px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
                        <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
                            <h2 className="text-[24px] font-medium text-[#191f28]">비밀번호 변경</h2>
                            <button type="button" onClick={() => { setIsPasswordResetModalOpen(false); setPasswordResetForm({ cpId: '', password: '' }); }} className="p-2 text-[#adb5bd] hover:text-[#191f28]"><X size={24} /></button>
                        </div>
                        <div className="py-5 px-8 space-y-4">
                            <div className="p-4 bg-[var(--brand-primary-soft)] rounded-2xl flex items-center gap-3 border border-[var(--brand-primary-border)]">
                                <div className="p-2 bg-white rounded-lg"><Lock size={16} className="text-[var(--brand-primary)]" /></div>
                                <span className="text-[13px] font-medium text-[var(--brand-primary)]">가맹점 비밀번호를 변경합니다.</span>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-[13px] font-medium text-[#8b95a1]">가맹점 ID</label>
                                <input type="text" value={passwordResetForm.cpId} disabled className="w-full bg-[#f2f4f6] px-4 py-3 rounded-xl border border-transparent text-[15px] font-medium cursor-not-allowed opacity-60" />
                            </div>
                            <ModalInput label="새 비밀번호" placeholder="NewPass1234!" type="password" icon={<Lock size={16} />} value={passwordResetForm.password} onChange={(e) => setPasswordResetForm((p) => ({ ...p, password: e.target.value }))} />
                        </div>
                        <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
                            <button type="button" onClick={() => { setIsPasswordResetModalOpen(false); setPasswordResetForm({ cpId: '', password: '' }); }} disabled={isResettingPassword} className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-medium text-[16px] disabled:opacity-50 disabled:cursor-not-allowed">취소</button>
                            <button
                                type="button"
                                onClick={async () => {
                                    if (!passwordResetForm.cpId?.trim()) { showToastRef.current('가맹점 ID가 없습니다.', 'error'); return; }
                                    if (!passwordResetForm.password?.trim()) { showToastRef.current('새 비밀번호를 입력해주세요.', 'error'); return; }
                                    setIsResettingPassword(true);
                                    try {
                                        await resetAdminCpPassword({ cpId: passwordResetForm.cpId.trim(), password: passwordResetForm.password.trim() });
                                        showToastRef.current('비밀번호가 성공적으로 변경되었습니다.');
                                        setIsPasswordResetModalOpen(false);
                                        setPasswordResetForm({ cpId: '', password: '' });
                                    } catch (e) {
                                        showToastRef.current(e.message || '비밀번호 변경에 실패했습니다.', 'error');
                                    } finally {
                                        setIsResettingPassword(false);
                                    }
                                }}
                                disabled={isResettingPassword}
                                className="flex-1 py-4 bg-[var(--brand-primary)] text-white rounded-2xl font-medium text-[16px] shadow-lg shadow-[var(--brand-primary)]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isResettingPassword ? '변경 중...' : '변경 완료'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MerchantManage_2;
